import React, { useCallback, useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { post, get } from 'aws-amplify/api'
import format from 'date-fns/format'

import HeaderContext from '../context/HeaderContext'
import Backdrop from '../components/UI/Backdrop/Backdrop'
import Spinner from '../components/UI/Spinner/SpinnerPlenna'
import { pushDataLayerEvent } from '../utils/dataLayerEvents'
import {
  buildPatientIdentifyTraits,
  identifyPatient
} from '../utils/identifyPatient'
import { getAge } from '../utils/formatDates'
import errorTrackingService from '../services/errorTrackingService'

const modes = {
  'Cuidar mi salud': 'default',
  'Quedar embarazada': 'default',
  'Seguir mi embarazo': 'pregnancy'
}

const HeaderProvider = ({ children, setMode }) => {
  const [headerType, setHeaderType] = useState('CenterBasic')
  const [variant, setVariant] = useState('')
  const [title, setTitle] = useState('')
  const [fum, setFum] = useState('')
  const [userInfo, setUserInfo] = useState({
    name: '',
    photo: '',
    mode: ''
  })
  const [loading, setLoading] = useState(false)
  const { getIdTokenClaims, isAuthenticated, user } = useAuth0()
  const [iconWhite, setIconWhite] = useState(false)
  const [featureFlags, setFeatureFlags] = useState(null)
  const [onHeaderBack, setOnHeaderBackState] = useState(null)
  const [headerSurface, setHeaderSurface] = useState(null)

  /**
   * Store a back handler without React treating it as a useState updater.
   * setState(fn) invokes fn(prev) — passing goBack directly would run navigation on mount.
   */
  const setOnHeaderBack = useCallback((handler) => {
    if (typeof handler === 'function') {
      setOnHeaderBackState(() => handler)
    } else {
      setOnHeaderBackState(null)
    }
  }, [])

  const baseUrl = process.env.REACT_APP_API_BASE_URL

  const getPatientInfo = async () => {
    const idToken = await getIdTokenClaims()
    try {
      const resp = post({
        apiName: baseUrl,
        path: '/auth/getPatientInfo',
        options: {
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${idToken.__raw}`
          },
          body: {}
        }
      })
      const { body } = await resp.response
      const data = await body.json()
      return data
    } catch (error) {
      console.log(error)
    }
  }

  const getQuarter = (week) => {
    if (week <= 12) return 1
    if (week <= 24) return 2
    return 3
  }

  const updatePatientInfo = () => {
    getPatientInfo().then((resp) => {
      const data = resp.data
      setUserInfo({
        email: user.email,
        grief: data?.grief,
        name: data?.given_name,
        objective: data?.objective,
        stage: data?.stage,
        photo: user.picture,
        logins: user.loginCount,
        mode: modes[data?.objective] || 'default',
        gestationalWeek: data?.gestational_week,
        gestationalDay: data?.gestational_day,
        quarter: getQuarter(parseInt(data?.gestational_week)),
        illustration: data?.illustration,
        firstName: data?.given_name,
        lastName: data?.family_name,
        phone: data?.phone_number,
        country: data?.country,
        state: data?.state,
        postalCode: data?.zip_code,
        birthdate: data?.birthdate
          ? format(new Date(data?.birthdate), 'dd/MM/yyyy')
          : '',
        age: data?.birthdate ? getAge(new Date(data?.birthdate)) : '',
        showClinics: data?.multiclinic,
        feature_flags: data?.feature_flags,
        subscription: data?.subscription,
        show_privacy: data?.show_privacy ?? false
      })
      identifyPatient(
        user.app_metadata.patient_id,
        buildPatientIdentifyTraits({ user, data })
      )

      errorTrackingService.setUserInfo({
        userId: user.app_metadata.patient_id,
        email: user.email,
        name: data?.given_name,
        objective: data?.objective,
        mode: modes[data?.objective] || 'default'
      })

      setLoading(false)
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        userId: resp?.data?._id || ''
      })

      pushDataLayerEvent('user_data_loaded', {
        userId: resp?.data?._id || '',
        email: user.email || '',
        firstName: data?.given_name || '',
        lastName: data?.family_name || '',
        objective: data?.objective || '',
        loginCount: user.loginCount || 0
      })
    })
  }

  const getFeatureFlags = async () => {
    const idToken = await getIdTokenClaims()
    try {
      const resp = get({
        apiName: baseUrl,
        path: '/api/v2/patients/feature-flags',
        options: {
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${idToken.__raw}`
          }
        }
      })
      const { body } = await resp.response
      const data = await body.json()
      return data
    } catch (error) {
      console.log(error)
    }
  }

  const updateFeatureFlags = () => {
    getFeatureFlags().then((resp) => {
      setFeatureFlags(resp.data)
    })
  }

  useEffect(() => {
    if (isAuthenticated) {
      setLoading(true)
      updatePatientInfo()
      getFeatureFlags().then((resp) => {
        const data = resp.data
        setFeatureFlags(data)
      })
    }
    // eslint-disable-next-line
  }, [isAuthenticated]);

  useEffect(() => {
    if (user && userInfo?.firstName && fum !== '') {
      identifyPatient(
        user.app_metadata.patient_id,
        buildPatientIdentifyTraits({ user, data: userInfo, fum })
      )
    }
  }, [userInfo, fum, user])

  useEffect(() => {
    document.documentElement.setAttribute('mode', userInfo.mode)
    setMode(userInfo.mode)
  }, [userInfo, setMode])

  return (
    <HeaderContext.Provider
      value={{
        headerType,
        setHeaderType,
        variant,
        setVariant,
        title,
        setTitle,
        userInfo,
        setUserInfo,
        updatePatientInfo,
        setFum,
        iconWhite,
        setIconWhite,
        featureFlags,
        setFeatureFlags,
        updateFeatureFlags,
        onHeaderBack,
        setOnHeaderBack,
        headerSurface,
        setHeaderSurface
      }}
    >
      {loading
        ? (
            <Backdrop show>
              <Spinner />
            </Backdrop>
          )
        : (
            children
          )}
    </HeaderContext.Provider>
  )
}

export default HeaderProvider
